/**
 * Textos de fábrica de las secciones del builder: español EXACTO (clave) →
 * inglés de EE.UU. (valor). SOLO para tenants de EE.UU. (`clients.country = 'US'`).
 *
 * Lo usa builder2/utils/defaultsEn.ts para completar, al renderizar, las props
 * que el árbol guardado NO trae y que Craft llenaría con `craft.props` de cada
 * componente (en español). Muchas secciones se guardan solo con sus colores
 * (FAQ, CTAModerno, HeroBasic, VehicleCarousel, los botones de WhatsApp…) y
 * leen sus textos de esos defaults: un sitio de EE.UU. los mostraba en español.
 *
 * Las claves salen de `craft.props` de los componentes del resolver
 * (`baseResolver` de src/app/BuilderRenderer.tsx); el harness
 * `test:sitio-en-usa` falla si aparece un texto de fábrica en español sin
 * entrada acá.
 *
 * Reglas (las mismas del mapa de Brasil, defaultsPt.{a,b}.ts):
 * - La clave es el texto TAL CUAL está en el código.
 * - Solo texto que se ve (o que recibe la automotora, como el mensaje
 *   prellenado de WhatsApp). NUNCA valores que el código compara ('Publicado',
 *   'Reservado', 'Vendido', 'Nuevo', 'Todos'…), ni números de las cifras
 *   ('2,000': el sitio los anima con parseInt), ni nombres de componente,
 *   íconos o URLs.
 * - Nombres propios de los testimonios y del equipo quedan como están.
 * - Nombres de las páginas: los mismos del navbar de EE.UU. (Home, Financing,
 *   Consignment, Sell Your Car, Car Finder, Book a Visit, Contact).
 */
export const DEFAULTS_EN: Record<string, string> = {
  // ── userComponents/ ────────────────────────────────────────────────────────
  'Click aquí': 'Click here',
  'Título de sección': 'Section title',
  'Un título que acompaña la imagen': 'A title to go with the image',
  'Contá acá lo que quieras destacar. Este bloque combina una imagen con texto y un botón opcional.':
    "Tell us here what you'd like to highlight. This block combines an image with text and an optional button.",
  'Título de la tarjeta': 'Card title',
  'Una descripción breve de lo que ofrece esta tarjeta.': 'A short description of what this card offers.',
  'Primer punto de la lista': 'First list item',
  'Segundo punto de la lista': 'Second list item',
  'Tercer punto de la lista': 'Third list item',
  'Deportivo de lujo': 'Luxury sports car',
  'Elegancia en movimiento': 'Elegance in motion',
  'Potencia y diseño': 'Power and design',
  '¿Cómo funciona el financiamiento?': 'How does financing work?',
  'Trabajamos con las principales instituciones y la aprobación suele salir en 24 horas.':
    'We work with leading lenders, and approval usually comes through within 24 hours.',
  '¿Los vehículos tienen garantía?': 'Do the vehicles come with a warranty?',
  'Todos incluyen 6 meses de garantía mecánica.': 'All of them include a 6-month mechanical warranty.',
  '¿Reciben mi auto en parte de pago?': 'Do you take trade-ins?',
  'Sí, lo tasamos sin costo y lo tomamos como pie.': 'Yes. We appraise it for free and apply it to your down payment.',
  'Un beneficio destacado': 'A standout benefit',
  'Explicá en una línea por qué le conviene al cliente.': "Explain in one line why it's a great fit for the customer.",
  'Escribinos por WhatsApp': 'Message us on WhatsApp',
  'Hola, me interesa saber más sobre sus vehículos.': "Hi, I'd like to know more about your vehicles.",

  // ── initialfold/ (héroes) ──────────────────────────────────────────────────
  'Encuentra el auto ideal para ti': 'Find the right car for you',
  'Amplio catálogo de vehículos seminuevos certificados con garantía y financiamiento a tu medida':
    'A wide selection of certified pre-owned vehicles with warranty and financing tailored to you',
  'Ver vehículos': 'View vehicles',
  'Contactar': 'Contact us',
  'Financiamiento a tu medida': 'Financing tailored to you',
  'Obtén tu auto seminuevo con opciones de financiamiento accesibles y aprobación rápida':
    'Get your pre-owned car with affordable financing options and fast approval',
  'Ver opciones': 'See options',
  'Encuentra tu próximo auto': 'Find your next car',
  'Amplio inventario de autos seminuevos verificados y con garantía':
    'A wide inventory of inspected pre-owned cars with warranty',
  'GoAuto - Tu mejor opción': 'GoAuto - Your best choice',
  'Encuentra el vehículo perfecto para ti': 'Find the perfect vehicle for you',
  'Financiamiento disponible': 'Financing available',
  'Ofrecemos las mejores opciones de financiamiento adaptadas a tus necesidades. Aprobación rápida y tasas competitivas.':
    'We offer the best financing options tailored to your needs. Fast approval and competitive rates.',
  'Solicitar ahora': 'Apply now',
  'Encuentra tu auto ideal': 'Find your ideal car',
  'Busca entre miles de vehículos disponibles': 'Search thousands of available vehicles',
  'Buscar': 'Search',
  'Lo mejor para tu próximo auto': 'The best for your next car',
  'Descubre por qué miles de personas eligen GoAuto para encontrar vehículos':
    'Discover why thousands of people choose GoAuto to find their vehicle',
  'Opciones flexibles de pago adaptadas a tu presupuesto y necesidades':
    'Flexible payment options tailored to your budget and needs',
  'Amplio catálogo': 'Wide selection',
  'Miles de vehículos nuevos y usados con toda la información que necesitas':
    'Thousands of new and used vehicles with all the information you need',
  'Proceso rápido': 'Fast process',
  'Desde la búsqueda hasta la adquisición en tiempo récord': 'From search to purchase in record time',
  'Garantía total': 'Full warranty',
  'Todos nuestros vehículos pasan por rigurosas inspecciones de calidad':
    'All our vehicles go through rigorous quality inspections',
  'Lo que nuestros clientes dicen': 'What our customers say',
  'Miles de personas han encontrado su vehículo ideal con GoAuto':
    'Thousands of people have found their ideal vehicle with GoAuto',
  'Comprador Reciente': 'Recent Buyer',
  'Encontré mi auto soñado en menos de una semana. El proceso fue increíblemente sencillo y el financiamiento se ajustó perfectamente a mi presupuesto.':
    'I found my dream car in less than a week. The process was incredibly simple and the financing fit my budget perfectly.',
  'Cliente Satisfecha': 'Happy Customer',
  'La atención personalizada fue excepcional. Me ayudaron a entender todas mis opciones y encontré un vehículo que superó mis expectativas.':
    'The personalized service was exceptional. They helped me understand all my options, and I found a vehicle that exceeded my expectations.',
  'Padre de Familia': 'Family Man',
  'Necesitaba una camioneta familiar con excelentes condiciones y a buen precio. GoAuto me ofreció múltiples opciones que se ajustaban a mis necesidades.':
    'I needed a family SUV in excellent condition at a good price. GoAuto offered me several options that fit my needs.',
  'Miles de vehículos disponibles en toda la región': 'Thousands of vehicles available across the region',
  'Marca': 'Make',
  'Modelo': 'Model',
  'Año': 'Year',
  'Precio': 'Price',
  'La mejor selección de autos seminuevos y nuevos': 'The best selection of new and pre-owned cars',
  'Encuentra el vehículo perfecto para ti. Con miles de opciones disponibles, garantía extendida y financiamiento accesible, comprar tu próximo auto nunca fue tan fácil.':
    'Find the perfect vehicle for you. With thousands of options, an extended warranty and affordable financing, buying your next car has never been easier.',
  'Ver catálogo': 'View inventory',
  'Solicitar cotización': 'Request a quote',
  'Autos certificados': 'Certified cars',
  'Todos nuestros vehículos son inspeccionados minuciosamente': 'All our vehicles are thoroughly inspected',
  'Garantía extendida': 'Extended warranty',
  'Hasta 3 años de garantía en motor y transmisión': 'Up to 3 years of engine and transmission warranty',
  'Compra segura': 'Secure purchase',
  'Documentación verificada y proceso transparente': 'Verified paperwork and a transparent process',
  'Automotora': 'Car Dealership',
  'Ver Stock Completo': 'View Full Inventory',
  'Encuentra tu próximo vehículo en': 'Find your next vehicle at',
  'Describe el vehículo de tus sueños y deja que nuestra IA encuentre las mejores opciones para ti.':
    'Describe your dream vehicle and let our AI find the best options for you.',
  'Toyota Corolla blanco': 'White Toyota Corolla',
  'Descubre Tu Próximo Vehículo': 'Discover Your Next Vehicle',
  'Explora nuestra selección premium de autos y encuentra el que se adapta a tu estilo de vida':
    'Explore our premium selection of cars and find the one that fits your lifestyle',
  'Encuentra tu': 'Find your',
  'Próximo vehículo': 'Next vehicle',

  // ── Navegación (navbars, HeroMega, footers): los nombres de página de EE.UU. ──
  'Inicio': 'Home',
  'Stock disponible': 'Inventory',
  'Consignación': 'Consignment',
  'Consignaciones': 'Consignment',
  'Compramos tu auto': 'Sell Your Car',
  'Compra directa': 'Sell Your Car',
  'Buscamos por ti': 'Car Finder',
  'Quiénes somos': 'About Us',
  'Sobre nosotros': 'About Us',
  'Contacto': 'Contact',
  'Financiamiento': 'Financing',
  'Vehículos': 'Vehicles',
  'Empresa': 'Company',
  'Servicios': 'Services',
  'Redes sociales': 'Social media',
  'Tu aliado de confianza en la compra de vehículos.': 'Your trusted partner in buying a vehicle.',

  // ── vehicles/ ──────────────────────────────────────────────────────────────
  'Nuestros vehículos': 'Our vehicles',
  'Explora nuestro inventario de vehículos disponibles': 'Browse our inventory of available vehicles',
  'Ver detalles': 'View details',
  'Recién publicado': 'Just listed',
  'Vehículos destacados': 'Featured Vehicles',
  'Descubre nuestra selección de vehículos': 'Discover our selection of vehicles',
  'Ver todos': 'View all',
  'Sin resultados': 'No results',
  'No encontramos vehículos con esos filtros': 'No vehicles match those filters',
  'Ver todos los vehículos': 'View all vehicles',

  // ── features/ (por qué elegirnos, FAQ) ─────────────────────────────────────
  'Nuestras ventajas': 'Our advantages',
  '¿Por qué elegirnos?': 'Why choose us?',
  'Autos inspeccionados y garantizados': 'Inspected and guaranteed cars',
  'Cada vehículo pasa por un riguroso proceso de inspección para garantizar tu seguridad y tranquilidad.':
    'Every vehicle goes through a rigorous inspection process to ensure your safety and peace of mind.',
  'Planes de financiamiento flexibles diseñados para adaptarse a tus necesidades y presupuesto.':
    'Flexible financing plans designed to fit your needs and budget.',
  'Atención personalizada': 'Personalized service',
  'Un asesor dedicado te acompaña en cada paso del proceso de compra.':
    'A dedicated advisor guides you through every step of the buying process.',
  'Preguntas frecuentes': 'Frequently asked questions',
  'Preguntas Frecuentes': 'Frequently Asked Questions',
  '¿Qué tipos de vehículos ofrecen?': 'What types of vehicles do you offer?',
  'Ofrecemos una amplia gama de vehículos, desde compactos hasta SUVs y vehículos de lujo. Todos nuestros vehículos son seminuevos y han pasado por un riguroso proceso de inspección.':
    'We offer a wide range of vehicles, from compacts to SUVs and luxury models. All our vehicles are pre-owned and have passed a rigorous inspection process.',
  'Trabajamos con diversas entidades financieras para ofrecer opciones que se adapten a tus necesidades. Nuestros asesores pueden ayudarte a encontrar el plan más adecuado según tu situación financiera.':
    'We work with several lenders to offer options that fit your needs. Our advisors can help you find the right plan for your financial situation.',
  '¿Ofrecen garantía en sus vehículos?': 'Do your vehicles come with a warranty?',
  'Sí, todos nuestros vehículos incluyen garantía. La duración y cobertura pueden variar según el modelo, año y estado del vehículo. Consulta con nuestros asesores para más detalles.':
    'Yes, all our vehicles include a warranty. Length and coverage may vary by model, year and condition. Ask our advisors for details.',
  'Descubre por qué nuestros clientes confían en nosotros': 'Discover why our customers trust us',
  'Garantía': 'Warranty',
  'Todos nuestros vehículos cuentan con garantía': 'All our vehicles come with a warranty',
  'Opciones de financiamiento flexibles': 'Flexible financing options',
  'Calidad': 'Quality',
  'Vehículos seleccionados y certificados': 'Carefully selected and certified vehicles',

  // ── testimonials/ ──────────────────────────────────────────────────────────
  'Testimonios': 'Testimonials',
  'Lo que dicen nuestros clientes': 'What our customers say',
  'Comprador de Toyota Corolla': 'Toyota Corolla buyer',
  'Excelente servicio de principio a fin. El proceso de compra fue rápido y transparente. Estoy muy contento con mi nuevo auto.':
    "Excellent service from start to finish. The buying process was fast and transparent. I'm very happy with my new car.",
  'Compradora de Honda CR-V': 'Honda CR-V buyer',
  'El asesor fue muy paciente y resolvió todas mis dudas. Me ayudaron a conseguir el financiamiento perfecto para mis necesidades.':
    'The advisor was very patient and answered all my questions. They helped me get the perfect financing for my needs.',
  'Comprador de Nissan Sentra': 'Nissan Sentra buyer',
  'La garantía y el servicio post-venta son excelentes. Me han atendido siempre que lo he necesitado sin problemas.':
    "The warranty and after-sales service are excellent. They've always taken care of me whenever I needed it.",

  // ── contact/ (CTA, cómo llegar, WhatsApp flotante) ─────────────────────────
  '¿Listo para encontrar tu auto ideal?': 'Ready to find your ideal car?',
  'Contáctanos hoy mismo': 'Contact us today',
  'Contáctanos': 'Contact us',
  '¿Listo para encontrar tu próximo vehículo?': 'Ready to find your next vehicle?',
  'Contáctanos hoy mismo.': 'Contact us today.',
  '¿Cómo llegar?': 'How to find us',
  'Encuéntranos en la siguiente dirección:': 'Find us at the following address:',
  'Cómo llegar': 'Get directions',
  'Hola, me interesa saber más sobre sus vehículos disponibles.':
    "Hi, I'd like to know more about your available vehicles.",
  '¿Tienes dudas? Escríbenos': 'Questions? Message us',

  // ── videos/ ────────────────────────────────────────────────────────────────
  'Título del Video Impactante': 'Your Standout Video Title',
  'Atrae a tus visitantes con contenido visual atractivo.': 'Engage your visitors with eye-catching visual content.',

  // ── moderna/ ───────────────────────────────────────────────────────────────
  'La mejor selección de vehículos con financiamiento a tu medida y atención personalizada.':
    'The best selection of vehicles, with financing tailored to you and personalized service.',
  '500+ autos vendidos': '500+ cars sold',
  '4.9★ en Google': '4.9★ on Google',
  'Financiamiento 100%': '100% financing',
  'Garantía incluida': 'Warranty included',
  'Vehículos vendidos': 'Vehicles sold',
  'Clientes satisfechos': 'Happy customers',
  'Años de experiencia': 'Years of experience',
  'Rating en Google': 'Google rating',
  'Experiencias reales de quienes confiaron en nosotros': 'Real experiences from people who trusted us',
  'Excelente experiencia de compra. El equipo fue muy profesional y encontré exactamente lo que buscaba en menos de una semana.':
    'Excellent buying experience. The team was very professional, and I found exactly what I was looking for in less than a week.',
  'Cliente desde 2022': 'Customer since 2022',
  'Cliente desde 2023': 'Customer since 2023',
  'Cliente desde 2024': 'Customer since 2024',
  'El mejor servicio postventa que he tenido. Respuesta inmediata y soluciones reales a cada consulta que tuve.':
    "The best after-sales service I've ever had. Immediate answers and real solutions to every question I had.",
  'Financiamiento rápido y transparente. Me dieron las mejores condiciones del mercado sin letra chica.':
    'Fast, transparent financing. They gave me the best terms on the market, with no fine print.',
  'Contáctanos hoy y descubre las mejores opciones de financiamiento disponibles para ti.':
    'Contact us today and discover the best financing options available to you.',
  'Comenzar ahora': 'Get started',

  // ── premium/ ───────────────────────────────────────────────────────────────
  'Experiencia automotriz': 'The automotive experience',
  'Vehículos seleccionados para quienes exigen lo extraordinario.':
    'Vehicles selected for those who demand the extraordinary.',
  'Explorar vehículos': 'Explore vehicles',
  'Por qué elegirnos': 'Why choose us',
  'Una experiencia premium en cada detalle': 'A premium experience in every detail',
  'Nos dedicamos a ofrecer el más alto estándar en cada paso del proceso.':
    "We're dedicated to delivering the highest standard at every step of the process.",
  'Cada vehículo cuenta con cobertura completa respaldada por los mejores estándares de la industria.':
    'Every vehicle comes with full coverage backed by the best industry standards.',
  'Financiamiento flexible': 'Flexible financing',
  'Planes personalizados con las mejores tasas del mercado.': 'Personalized plans with the best rates on the market.',
  'Atención 24/7': '24/7 support',
  'Soporte dedicado cuando lo necesites, sin importar la hora.': 'Dedicated support whenever you need it, at any hour.',
  'Certificación premium': 'Premium certification',
  'Cada vehículo supera 150 puntos de inspección rigurosa.': 'Every vehicle passes a rigorous 150-point inspection.',
  'Entrega a domicilio': 'Home delivery',
  'Tu vehículo llega directamente a tu puerta.': 'Your vehicle is delivered right to your door.',
  'Asesor personal': 'Personal advisor',
  'Un experto dedicado exclusivamente a tu búsqueda.': 'An expert dedicated exclusively to your search.',
  'Una experiencia de compra excepcional. Desde la primera consulta hasta la entrega, cada detalle fue impecable.':
    'An exceptional buying experience. From the first inquiry to delivery, every detail was flawless.',
  'Empresaria': 'Business owner',
  'El nivel de profesionalismo y la calidad de los vehículos superaron mis expectativas. Sin duda volveré.':
    "The professionalism and the quality of the vehicles exceeded my expectations. I'll definitely be back.",
  'Ingeniero': 'Engineer',
  'Financiamiento transparente y un servicio post-venta que realmente se preocupa. Los recomiendo completamente.':
    'Transparent financing and after-sales service that truly cares. I highly recommend them.',
  'Arquitecta': 'Architect',
  'Nuestra galería': 'Our gallery',
  'Descubre nuestras instalaciones y vehículos destacados': 'Discover our facilities and featured vehicles',
  'Clásico moderno': 'Modern classic',
  'Agenda tu visita exclusiva': 'Book your exclusive visit',
  'Reserva una cita personalizada con uno de nuestros asesores y vive la experiencia premium que mereces.':
    'Book a personal appointment with one of our advisors and enjoy the premium experience you deserve.',
  'Reservar ahora': 'Book now',

  // ── marketing/ (cifras, promo, premios, confianza, logos) ──────────────────
  'Satisfacción del cliente': 'Customer satisfaction',
  'Tiempo de respuesta': 'Response time',
  '¡Oferta especial! Aprovecha nuestras promociones exclusivas este mes.':
    'Special offer! Take advantage of our exclusive deals this month.',
  'Ver ofertas': 'View deals',
  'Reconocimientos': 'Awards',
  'Reconocimientos y certificaciones': 'Awards and certifications',
  'Nuestro trabajo respaldado por las principales instituciones del rubro':
    'Our work, backed by the leading institutions in the industry',
  'Mejor Automotora': 'Best Dealership',
  'Reconocimiento al servicio al cliente': 'Recognized for customer service',
  'Concesionario Certificado': 'Certified Dealer',
  'Estándares de calidad internacionales': 'International quality standards',
  'Premio Excelencia': 'Excellence Award',
  'Otorgado por AutoChile': 'Industry recognition',
  '¿Por qué confiar en nosotros?': 'Why trust us?',
  'Compromisos concretos con cada cliente': 'Concrete commitments to every customer',
  'Garantía 12 meses': '12-month warranty',
  'Cobertura completa de motor y transmisión': 'Full engine and transmission coverage',
  'Inspección 150 puntos': '150-point inspection',
  'Cada vehículo revisado por mecánicos certificados': 'Every vehicle checked by certified mechanics',
  'Devolución 7 días': '7-day returns',
  'Si no quedás conforme, te devolvemos tu plata': "If you're not satisfied, we'll give you your money back",
  'Financiamiento aprobado': 'Financing approved',
  'En 24 horas, con o sin pie': 'Within 24 hours, with or without a down payment',
  'Marcas que trabajamos': 'Brands we carry',
  'Confían en nosotros las marcas más reconocidas del mercado': 'Trusted by the best-known brands on the market',
  'Marca 1': 'Brand 1',
  'Marca 2': 'Brand 2',
  'Marca 3': 'Brand 3',
  'Marca 4': 'Brand 4',
  'Marca 5': 'Brand 5',
  'Marca 6': 'Brand 6',
  'Marca 7': 'Brand 7',
  'Marca 8': 'Brand 8',
  'Con el respaldo de los mejores': 'Backed by the best',
  'Trabajamos con empresas e instituciones de primer nivel': 'We work with top-tier companies and institutions',
  'Marcas que vendemos': 'Brands we sell',
  'Bancos partners': 'Partner banks',
  'Banco 1': 'Bank 1',
  'Banco 2': 'Bank 2',
  'Banco 3': 'Bank 3',
  'Certificaciones': 'Certifications',

  // ── media/ y team/ (vienen sin acentos en el código) ───────────────────────
  'Nuestra Galeria': 'Our Gallery',
  'Deportivo de lujo en exhibicion': 'Luxury sports car on display',
  'Clasico americano restaurado': 'Restored American classic',
  'Sedan moderno con tecnologia avanzada': 'Modern sedan with advanced technology',
  'Vehiculo deportivo rojo': 'Red sports car',
  'SUV familiar en carretera': 'Family SUV on the road',
  'Vista lateral de un Corvette': 'Side view of a Corvette',
  'Galería': 'Gallery',
  'Nuestro Equipo': 'Our Team',
  'Gerente General': 'General Manager',
  'Con mas de 15 anos de experiencia en la industria automotriz, Carlos lidera nuestro equipo con vision y dedicacion.':
    'With more than 15 years of experience in the automotive industry, Carlos leads our team with vision and dedication.',
  'Asesor de Ventas': 'Sales Advisor',
  'Especialista en atencion al cliente, Maria te ayudara a encontrar el vehiculo perfecto para tus necesidades.':
    'A customer service specialist, Maria will help you find the perfect vehicle for your needs.',
  'Servicio Tecnico': 'Service Technician',
  'Tecnico certificado con amplia experiencia en mantenimiento y reparacion de vehiculos de todas las marcas.':
    'Certified technician with broad experience maintaining and repairing vehicles of every make.',
};
